/* eslint-disable max-lines */
import { SOPTalkdown, SOPTalkdownType } from '@hakimo-ui/hakimo/types';
import {
  Button,
  Tooltip,
  SegmentedToggle,
  Label,
} from '@hakimo-ui/shared/ui-base';
import {
  PlusIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { TALKDOWN_TYPE_INFO } from '../constants';
import WorkflowFormTalkdownItem from '../WorkflowFormTalkdownItem';
import SOPCard from './SOPCard';
import { renderEditableList } from '../utils';
import { TALKDOWN_ENABLED_OPTIONS, INPUT_PLACEHOLDERS } from '../constants';
import { createStringListHandlers } from './utils';

interface Props {
  isTalkdownEnabled: boolean;
  talkdowns: SOPTalkdown[];
  talkdownNotes: string[];
  onChange: (data: {
    isTalkdownEnabled?: boolean;
    talkdowns?: SOPTalkdown[];
    talkdownNotes?: string[];
  }) => void;
  isEditing: boolean;
}

export function SOPTalkdownCard(props: Props) {
  const { isTalkdownEnabled, talkdowns, talkdownNotes, onChange, isEditing } =
    props;

  const handleToggleTalkdownEnabled = (value: string) => {
    onChange({ isTalkdownEnabled: value === 'enable' });
  };

  const handleTalkdownChange = (index: number) => (updatedVal: SOPTalkdown) => {
    const updatedTalkdowns = [...talkdowns];
    updatedTalkdowns[index] = updatedVal;
    onChange({ talkdowns: updatedTalkdowns });
  };

  const handleDeleteTalkdown = (index: number) => () => {
    const updatedTalkdowns = [...talkdowns];
    updatedTalkdowns.splice(index, 1);
    onChange({ talkdowns: updatedTalkdowns });
  };

  const handleAddTalkdown = () => {
    const updatedTalkdowns = [...talkdowns];
    updatedTalkdowns.push({ type: SOPTalkdownType.STATIC, text: '' });
    onChange({ talkdowns: updatedTalkdowns });
  };

  const {
    handleAdd: handleAddTalkdownNote,
    handleEdit: handleTalkdownNoteChange,
    handleDelete: handleDeleteTalkdownNote,
  } = createStringListHandlers(talkdownNotes, (updatedNotes: string[]) =>
    onChange({ talkdownNotes: updatedNotes })
  );

  const headerAction =
    isEditing && isTalkdownEnabled ? (
      <Button
        variant="primary"
        onClick={handleAddTalkdown}
        className="flex items-center gap-2"
      >
        <PlusIcon className="h-4 w-4" />
        Add
      </Button>
    ) : null;

  return (
    <SOPCard title="Talkdowns" headerAction={headerAction}>
      {isEditing ? (
        <div className="space-y-6">
          {/* Talkdown Enabled Segmented Control */}
          <div>
            <SegmentedToggle
              value={isTalkdownEnabled ? 'enable' : 'disable'}
              options={TALKDOWN_ENABLED_OPTIONS}
              onChange={handleToggleTalkdownEnabled}
              size="small"
            />
          </div>

          {/* Talkdowns Section */}
          {isTalkdownEnabled && (
            <div className="space-y-4">
              <div className="space-y-4">
                {talkdowns.map((talkdown, i) => (
                  <WorkflowFormTalkdownItem
                    key={i}
                    talkdown={talkdown}
                    onChange={handleTalkdownChange(i)}
                    onDelete={handleDeleteTalkdown(i)}
                    isDeleteEnabled={talkdowns.length > 1}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Talkdown Notes Section */}
          {isTalkdownEnabled && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-onlight-text-1 dark:text-ondark-text-1 font-semibold">
                  Notes
                </h4>
                <Button
                  variant="primary"
                  onClick={handleAddTalkdownNote}
                  className="flex items-center gap-2"
                >
                  <PlusIcon className="h-4 w-4" />
                  Add
                </Button>
              </div>
              {renderEditableList(
                talkdownNotes,
                handleTalkdownNoteChange,
                handleDeleteTalkdownNote,
                INPUT_PLACEHOLDERS.TALKDOWN_NOTE
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          <div>
            {isTalkdownEnabled ? (
              <Label type="success" small>
                <div className="flex items-center gap-1">
                  <CheckCircleIcon className="h-5 w-5" />
                  <span>Enabled</span>
                </div>
              </Label>
            ) : (
              <Label type="default" small>
                <div className="flex items-center gap-1">
                  <XCircleIcon className="h-5 w-5" />
                  <span>Disabled</span>
                </div>
              </Label>
            )}
          </div>

          {isTalkdownEnabled && talkdowns && talkdowns.length > 0 && (
            <div className="space-y-6">
              <div className="space-y-6">
                {talkdowns.map((talkdown, i) => (
                  <div key={i}>
                    <div className="mb-2 flex items-center justify-start">
                      <span className="text-onlight-text-1 dark:text-ondark-text-1 font-medium">
                        Talkdown {i + 1}
                      </span>
                      <div className="ml-2 flex items-center gap-2">
                        <Label
                          small
                          text={
                            talkdown.type === SOPTalkdownType.STATIC
                              ? 'Static'
                              : 'Dynamic'
                          }
                        />
                        <Tooltip
                          text={TALKDOWN_TYPE_INFO}
                          colorModifier="info"
                          position="top-right"
                          size="large"
                        >
                          <InformationCircleIcon className="h-4 w-4" />
                        </Tooltip>
                      </div>
                    </div>
                    <p className="text-onlight-text-2 dark:text-ondark-text-2 whitespace-pre-wrap">
                      {talkdown.text}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {isTalkdownEnabled && talkdownNotes && talkdownNotes.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-onlight-text-1 dark:text-ondark-text-1 font-medium">
                Notes
              </h4>
              <ol className="list-inside list-decimal space-y-1">
                {talkdownNotes.map((note, i) => (
                  <li
                    key={i}
                    className="text-onlight-text-2 dark:text-ondark-text-2"
                  >
                    <span className="break-words">{note}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      )}
    </SOPCard>
  );
}

export default SOPTalkdownCard;
